import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Clock, Check, X, UserCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePrivateRooms } from '@/hooks/usePrivateRooms';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { useToast } from '@/hooks/use-toast';
import { AvatarDisplay } from '@/components/AvatarDisplay';

export function FriendRequestsSection() {
  const { user } = useAuth();
  const { friends, loading, respondToFriendRequest } = usePrivateRooms();
  const { toast } = useToast();

  // Get pending requests (incoming - where current user is the addressee)
  const incomingRequests = friends.filter(f => 
    f.status === 'pending' && f.addressee_id === user?.id
  );

  // Get outgoing requests (sent - where current user is the requester)
  const outgoingRequests = friends.filter(f => 
    f.status === 'pending' && f.requester_id === user?.id
  );

  // Get pending request user IDs for profile fetching
  const pendingUserIds = friends
    .filter(f => f.status === 'pending')
    .map(f => f.requester_id === user?.id ? f.addressee_id : f.requester_id);

  const { profiles: pendingProfiles } = usePlayerProfiles(pendingUserIds);

  const handleRespondToRequest = async (friendshipId: string, response: 'accepted' | 'blocked') => {
    try {
      await respondToFriendRequest(friendshipId, response);
      
      toast({
        title: "Success",
        description: response === 'accepted' ? "Friend request accepted!" : "Friend request declined",
      });
    } catch (error: any) {
      console.error('Error responding to friend request:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to respond to friend request",
        variant: "destructive",
      });
    }
  };

  // Don't show if no pending requests
  if (incomingRequests.length === 0 && outgoingRequests.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="w-5 h-5" />
          Friend Requests
        </CardTitle>
        <CardDescription>
          Manage your pending friend requests
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Incoming Friend Requests */}
        {incomingRequests.length > 0 && (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                Incoming Requests ({incomingRequests.length})
              </h4>
              <div className="space-y-2">
                {incomingRequests.map(request => {
                  const requesterProfile = pendingProfiles[request.requester_id];
                  return (
                    <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                      <div className="flex items-center gap-3">
                        <AvatarDisplay
                          avatarConfig={requesterProfile?.avatar_config}
                          avatarUrl={requesterProfile?.avatar_url}
                          username={requesterProfile?.username}
                          size="sm"
                        />
                        <div>
                          <p className="font-medium text-sm">
                            {requesterProfile?.display_name || requesterProfile?.username || 'Anonymous'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            @{requesterProfile?.username} • {requesterProfile?.elo_rating} ELO
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="default"
                          className="h-7 px-2"
                          onClick={() => handleRespondToRequest(request.id, 'accepted')}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Accept
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 px-2"
                          onClick={() => handleRespondToRequest(request.id, 'blocked')}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {outgoingRequests.length > 0 && <Separator />}
          </>
        )}

        {/* Outgoing Friend Requests */}
        {outgoingRequests.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              Sent Requests ({outgoingRequests.length})
            </h4>
            <div className="space-y-2">
              {outgoingRequests.map(request => {
                const addresseeProfile = pendingProfiles[request.addressee_id];
                return (
                  <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg bg-orange-50 dark:bg-orange-950/20">
                    <div className="flex items-center gap-3">
                      <AvatarDisplay
                        avatarConfig={addresseeProfile?.avatar_config}
                        avatarUrl={addresseeProfile?.avatar_url}
                        username={addresseeProfile?.username}
                        size="sm"
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {addresseeProfile?.display_name || addresseeProfile?.username || 'Anonymous'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          @{addresseeProfile?.username} • {addresseeProfile?.elo_rating} ELO
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      Pending
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
